import { useCallback, useRef, useState } from 'react';

import { deleteRfqFile } from 'src/features/rfq-builder/api/deleteRfqFile';
import { captureException } from 'src/services/reporting';
import { UploadedFile } from 'src/shared/types/uploadedFile';
import { S3UploadError, uploadToS3 } from 'src/shared/utils/uploadToS3';

import { getPresignedUploadUrl } from '../api/getPresignedUploadUrl';

type UploadStatus = 'pending' | 'uploading' | 'completed' | 'error';

export interface PendingFileWithCaption {
  /** Unique identifier for this pending file */
  id: string;
  /** The original File object */
  file: File;
  /** Caption for the file */
  caption: string;
  /** Current upload status */
  status: UploadStatus;
  /** Upload progress percentage (0-100) */
  progress: number;
  /** The uploaded file record (available when status is 'completed') */
  uploadedFile?: UploadedFile;
  /** Error message (available when status is 'error') */
  error?: string;
}

interface UseOptimisticFileUploadWithCaptionOptions {
  rfqId: number | undefined;
}

/**
 * Hook for optimistic file uploads with caption support.
 *
 * Files are uploaded when a caption is provided, not immediately when added.
 * This reduces perceived latency for the user while allowing captions to be added first.
 */
export function useOptimisticFileUploadWithCaption({
  rfqId
}: UseOptimisticFileUploadWithCaptionOptions) {
  const [pendingFiles, setPendingFiles] = useState<PendingFileWithCaption[]>(
    []
  );

  // Keep a ref to access current pendingFiles in callbacks
  const pendingFilesRef = useRef<PendingFileWithCaption[]>([]);

  pendingFilesRef.current = pendingFiles;

  /**
   * Update the progress of a pending file.
   */
  const updateProgress = useCallback(
    (pendingFileId: string, progress: number) => {
      setPendingFiles((prev) =>
        prev.map((pf) => (pf.id === pendingFileId ? { ...pf, progress } : pf))
      );
    },
    []
  );

  /**
   * Add files without immediately uploading them.
   * Files will be uploaded when uploadFileWithCaption is called.
   */
  const addFiles = useCallback((files: File[]) => {
    // Create pending file entries with 'pending' status
    const newPendingFiles: PendingFileWithCaption[] = files.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      caption: '',
      status: 'pending' as const,
      progress: 0
    }));

    setPendingFiles((prev) => [...prev, ...newPendingFiles]);
  }, []);

  /**
   * Upload a single file with its caption and update its status.
   */
  const uploadFileWithCaption = async (
    pendingFileId: string,
    caption: string
  ) => {
    if (!rfqId) return;

    const pendingFile = pendingFilesRef.current.find(
      (pf) => pf.id === pendingFileId
    );

    if (!pendingFile) return;

    // Update status to uploading
    setPendingFiles((prev) =>
      prev.map((pf) =>
        pf.id === pendingFileId
          ? { ...pf, status: 'uploading' as const, progress: 0, caption }
          : pf
      )
    );

    try {
      // Get presigned URL - this also creates the UploadedFile record on the backend
      const presignedData = await getPresignedUploadUrl({
        rfq_id: rfqId,
        filename: pendingFile.file.name,
        content_type: pendingFile.file.type || 'application/octet-stream',
        size: pendingFile.file.size,
        caption: caption || undefined
      });

      // Store uploadedFile immediately so we can delete it if user cancels during upload
      setPendingFiles((prev) =>
        prev.map((pf) =>
          pf.id === pendingFileId
            ? { ...pf, uploadedFile: presignedData.file }
            : pf
        )
      );

      // Upload to S3 with progress tracking
      await uploadToS3(
        presignedData.upload_url,
        presignedData.upload_fields,
        pendingFile.file,
        {
          onProgress: (progress) => {
            updateProgress(pendingFileId, progress);
          }
        }
      );

      // Update status to completed
      setPendingFiles((prev) =>
        prev.map((pf) =>
          pf.id === pendingFileId
            ? {
                ...pf,
                status: 'completed' as const,
                progress: 100
              }
            : pf
        )
      );
    } catch (error) {
      // Report to Sentry with full technical context before showing user-friendly message
      captureException({
        error: error instanceof Error ? error : new Error('Upload failed'),
        extra: {
          pendingFileId,
          rfqId,
          ...(error instanceof S3UploadError && {
            s3ErrorCode: error.s3ErrorCode,
            httpStatus: error.httpStatus,
            rawResponse: error.rawResponse
          })
        }
      });

      // Update status to error with user-friendly message
      setPendingFiles((prev) =>
        prev.map((pf) =>
          pf.id === pendingFileId
            ? {
                ...pf,
                status: 'error' as const,
                error: error instanceof Error ? error.message : 'Upload failed'
              }
            : pf
        )
      );
    }
  };

  /**
   * Retry a failed upload, letting the user recover from an error instead of only
   * removing the file.
   *
   * A fresh presigned URL is requested, since the previous one may have been the
   * problem (e.g. expired). That also creates a new UploadedFile record, so the one from
   * the failed attempt, which has no S3 object behind it, is deleted from the RFQ.
   */
  const retryUpload = async (pendingFileId: string) => {
    const pendingFile = pendingFilesRef.current.find(
      (pf) => pf.id === pendingFileId
    );

    if (!pendingFile || pendingFile.status !== 'error') return;

    if (rfqId && pendingFile.uploadedFile) {
      deleteRfqFile({
        rfq_id: rfqId,
        file_id: pendingFile.uploadedFile.id
      }).catch((error) => {
        // Log but don't block the retry - the orphaned record holds no content
        console.error('Failed to delete file from RFQ:', error);
      });
    }

    setPendingFiles((prev) =>
      prev.map((pf) =>
        pf.id === pendingFileId
          ? { ...pf, uploadedFile: undefined, error: undefined }
          : pf
      )
    );

    await uploadFileWithCaption(pendingFileId, pendingFile.caption);
  };

  /**
   * Update the caption of a pending file.
   */
  const updateCaption = useCallback(
    (pendingFileId: string, caption: string) => {
      setPendingFiles((prev) =>
        prev.map((pf) => (pf.id === pendingFileId ? { ...pf, caption } : pf))
      );
    },
    []
  );

  /**
   * Remove a file from the pending list.
   * Also deletes the file from the RFQ.
   * Note: If the file is still uploading, the upload will complete but the result will be ignored.
   */
  const removeFile = useCallback(
    (pendingFileId: string) => {
      // Find the file to check if it needs to be deleted from RFQ
      const fileToRemove = pendingFilesRef.current.find(
        (pf) => pf.id === pendingFileId
      );

      // Remove from local state immediately
      setPendingFiles((prev) => prev.filter((pf) => pf.id !== pendingFileId));

      // If file was uploaded, also delete from RFQ (fire and forget)
      if (rfqId && fileToRemove && fileToRemove.uploadedFile) {
        deleteRfqFile({
          rfq_id: rfqId,
          file_id: fileToRemove.uploadedFile.id
        }).catch((error) => {
          // Log but don't block - user already removed it from UI
          console.error('Failed to delete file from RFQ:', error);
        });
      }
    },
    [rfqId]
  );

  /**
   * Clear all files from the pending list.
   */
  const clearFiles = useCallback(() => {
    setPendingFiles([]);
  }, []);

  /**
   * Get file IDs for all completed uploads.
   * Returns null if any files are still uploading.
   */
  const getCompletedFileIds = useCallback((): string[] | null => {
    const hasUploading = pendingFiles.some((pf) => pf.status === 'uploading');

    if (hasUploading) return null;

    return pendingFiles
      .filter((pf) => pf.status === 'completed' && pf.uploadedFile)
      .map((pf) => pf.uploadedFile!.id.toString());
  }, [pendingFiles]);

  /**
   * Wait for all uploads to complete and return file IDs.
   * Polls every 100ms until all uploads are done.
   */
  const waitForUploads = useCallback(async (): Promise<string[]> => {
    return new Promise((resolve) => {
      const checkUploads = () => {
        const currentFiles = pendingFilesRef.current;
        const hasUploading = currentFiles.some(
          (pf) => pf.status === 'uploading'
        );

        if (!hasUploading) {
          const fileIds = currentFiles
            .filter((pf) => pf.status === 'completed' && pf.uploadedFile)
            .map((pf) => pf.uploadedFile!.id.toString());

          resolve(fileIds);
        } else {
          setTimeout(checkUploads, 100);
        }
      };

      checkUploads();
    });
  }, []);

  // Computed values
  const isUploading = pendingFiles.some((pf) => pf.status === 'uploading');
  const hasErrors = pendingFiles.some((pf) => pf.status === 'error');
  const allCompleted =
    pendingFiles.length > 0 &&
    pendingFiles.every((pf) => pf.status === 'completed');

  return {
    pendingFiles,
    addFiles,
    uploadFileWithCaption,
    retryUpload,
    updateCaption,
    removeFile,
    clearFiles,
    getCompletedFileIds,
    waitForUploads,
    isUploading,
    hasErrors,
    allCompleted
  };
}
